import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/*
 * WeatherBar — barra meteorológica horizontal sobre el header del portal.
 * 3 ciudades (Río Grande, Tolhuin, Ushuaia) con SVG animados sutiles.
 * Datos vienen de /api/weather (Open-Meteo cacheado 12 min en backend).
 * Respeta prefers-reduced-motion → sin animaciones si el usuario lo pidió.
 */

const REFRESH_MS = 15 * 60 * 1000; // 15 minutes — matches backend TTL (§3-4)

// ---------- SVG animated icons -----------------------------------------------
const Icon = ({ group, size = 22, reducedMotion, label }) => {
  const anim = !reducedMotion;
  const common = { width: size, height: size, viewBox: "0 0 24 24",
                   fill: "none", stroke: "currentColor", strokeWidth: 1.7,
                   strokeLinecap: "round", strokeLinejoin: "round",
                   "aria-hidden": "true", role: "img", "aria-label": label };
  switch (group) {
    case "clear":
    case "mostly_clear":
      return (
        <svg {...common} className={anim ? "spc-wx-sun" : ""}>
          <circle cx="12" cy="12" r="4" />
          <g className={anim ? "spc-wx-rays" : ""}>
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="22" y2="12" />
            <line x1="4.9" y1="4.9" x2="6.3" y2="6.3" />
            <line x1="17.7" y1="17.7" x2="19.1" y2="19.1" />
            <line x1="4.9" y1="19.1" x2="6.3" y2="17.7" />
            <line x1="17.7" y1="6.3" x2="19.1" y2="4.9" />
          </g>
        </svg>
      );
    case "partly_cloudy":
      return (
        <svg {...common}>
          <circle cx="7" cy="10" r="3" />
          <path className={anim ? "spc-wx-drift" : ""}
            d="M12 18h6a3 3 0 0 0 0-6 4 4 0 0 0-7.9-1" />
        </svg>
      );
    case "fog":
      return (
        <svg {...common}>
          <g className={anim ? "spc-wx-fog" : ""}>
            <line x1="3" y1="9"  x2="21" y2="9" />
            <line x1="3" y1="13" x2="19" y2="13" />
            <line x1="5" y1="17" x2="21" y2="17" />
          </g>
        </svg>
      );
    case "wind":
      return (
        <svg {...common}>
          <g className={anim ? "spc-wx-wind" : ""}>
            <path d="M4 10h10a3 3 0 1 0-3-3" />
            <path d="M4 14h13a3 3 0 1 1-3 3" />
          </g>
        </svg>
      );
    case "cloudy":
      return (
        <svg {...common}>
          <path className={anim ? "spc-wx-drift" : ""}
            d="M6 17h11a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.8-1.2A3.5 3.5 0 0 0 6 17z" />
        </svg>
      );
    case "rain":
    case "drizzle":
      return (
        <svg {...common}>
          <path d="M6 13h11a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.8-1.2A3.5 3.5 0 0 0 6 13z" />
          <g className={anim ? "spc-wx-rain" : ""}>
            <line x1="9" y1="16" x2="9" y2="19" />
            <line x1="12.5" y1="16" x2="12.5" y2="20" />
            <line x1="16" y1="16" x2="16" y2="19" />
          </g>
        </svg>
      );
    case "snow":
      return (
        <svg {...common}>
          <path d="M6 13h11a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.8-1.2A3.5 3.5 0 0 0 6 13z" />
          <g className={anim ? "spc-wx-snow" : ""}>
            <circle cx="9" cy="18" r="0.9" />
            <circle cx="12.5" cy="19" r="0.9" />
            <circle cx="16" cy="18" r="0.9" />
          </g>
        </svg>
      );
    case "storm":
      return (
        <svg {...common}>
          <path d="M6 13h11a3.5 3.5 0 0 0 0-7 5 5 0 0 0-9.8-1.2A3.5 3.5 0 0 0 6 13z" />
          <polyline className={anim ? "spc-wx-bolt" : ""}
            points="12,15 10,18 13,18 11,21" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
};

function useReducedMotion() {
  const [rm, setRm] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setRm(!!mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  return rm;
}

export default function WeatherBar() {
  const [data, setData] = useState(null);
  const reducedMotion = useReducedMotion();

  const load = useCallback(async () => {
    try {
      const r = await api.getWeather();
      setData(r);
    } catch (_e) { /* silent — never break the portal */ }
  }, []);

  useEffect(() => {
    // Defer initial load so it doesn't compete with LCP resources (§8).
    const idle = typeof window !== "undefined" && window.requestIdleCallback
      ? (fn) => window.requestIdleCallback(fn, { timeout: 1500 })
      : (fn) => setTimeout(fn, 300);
    let cancelIdle = idle(load);
    const t = setInterval(load, REFRESH_MS);
    return () => {
      clearInterval(t);
      if (typeof window !== "undefined" && window.cancelIdleCallback && typeof cancelIdle === "number") {
        try { window.cancelIdleCallback(cancelIdle); } catch (_e) { /* noop */ }
      }
    };
  }, [load]);

  const cities = (data && data.cities) || [];
  const fields = (data && data.widget_config && data.widget_config.fields) || ["temp", "label", "wind"];
  if (!cities.length) return null;

  const renderField = (c, key) => {
    switch (key) {
      case "temp":
        return (
          <span key="temp" className="font-mono tabular-nums text-white/90" data-testid={`wx-temp-${c.city_slug}`}>
            {c.temp_c == null ? "—" : `${Math.round(c.temp_c)}°`}
          </span>
        );
      case "feels_like":
        return c.feels_like_c == null ? null : (
          <span key="feels" className="hidden md:inline text-white/70 font-mono" title="Sensación térmica">
            ST {Math.round(c.feels_like_c)}°
          </span>
        );
      case "label":
        return <span key="label" className="hidden md:inline text-white/70">{c.label}</span>;
      case "wind":
        return c.wind_kmh == null ? null : (
          <span key="wind" className="hidden lg:inline text-white/70 font-mono" title="Viento">
            {Math.round(c.wind_kmh)} km/h
          </span>
        );
      case "gusts":
        return c.wind_gusts_kmh == null ? null : (
          <span key="gusts" className="hidden lg:inline text-white/70 font-mono" title="Ráfagas">
            R {Math.round(c.wind_gusts_kmh)}
          </span>
        );
      case "humidity":
        return c.humidity_pct == null ? null : (
          <span key="humi" className="hidden md:inline text-white/70 font-mono" title="Humedad">
            {c.humidity_pct}%
          </span>
        );
      case "precip":
        return c.precip_prob_pct == null ? null : (
          <span key="precip" className="hidden lg:inline text-white/70 font-mono" title="Probabilidad de precipitación">
            {c.precip_prob_pct}%↓
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="w-full bg-blueblack text-white text-xs"
      data-testid="weather-bar"
      role="region"
      aria-label="Clima actual en Tierra del Fuego"
    >
      <div className="container-wide mx-auto flex items-center justify-around sm:justify-center sm:gap-8 py-1.5 px-2">
        {cities.map((c) => (
          <div key={c.city_slug}
            data-testid={`weather-city-${c.city_slug}`}
            className={`flex items-center gap-1.5 sm:gap-2 min-w-0 ${c.stale ? "opacity-60" : ""}`}
            title={c.stale ? "Datos desactualizados temporalmente" : ""}
          >
            <span className="text-turquoise shrink-0">
              <Icon group={c.group} size={20} reducedMotion={reducedMotion}
                    label={c.label || "clima"} />
            </span>
            <span className="font-serif font-bold uppercase tracking-widest text-[10px] sm:text-[11px] truncate">{c.city}</span>
            {fields.map((f) => renderField(c, f))}
          </div>
        ))}
      </div>
      <style>{`
        .spc-wx-sun { transform-origin: 12px 12px; }
        @media (prefers-reduced-motion: no-preference) {
          .spc-wx-rays { animation: spcSpin 22s linear infinite; transform-origin: 12px 12px; }
          .spc-wx-drift { animation: spcDrift 6s ease-in-out infinite; }
          .spc-wx-rain line { animation: spcRain 1.2s linear infinite; opacity: .85; }
          .spc-wx-rain line:nth-child(2) { animation-delay: .3s; }
          .spc-wx-rain line:nth-child(3) { animation-delay: .6s; }
          .spc-wx-snow circle { animation: spcSnow 2.4s ease-in-out infinite; opacity: .85; }
          .spc-wx-snow circle:nth-child(2) { animation-delay: .5s; }
          .spc-wx-snow circle:nth-child(3) { animation-delay: 1s; }
          .spc-wx-bolt { animation: spcBolt 3.6s ease-in-out infinite; }
          .spc-wx-fog line { animation: spcFog 4.5s ease-in-out infinite; }
          .spc-wx-fog line:nth-child(2) { animation-delay: .7s; }
          .spc-wx-fog line:nth-child(3) { animation-delay: 1.4s; }
          .spc-wx-wind path { animation: spcWind 3.5s ease-in-out infinite; }
          .spc-wx-wind path:nth-child(2) { animation-delay: .6s; }
        }
        @keyframes spcSpin { to { transform: rotate(360deg); } }
        @keyframes spcDrift { 0%,100% { transform: translateX(0); } 50% { transform: translateX(1.2px); } }
        @keyframes spcRain { 0% { transform: translateY(-1px); opacity: 0; }
                             30% { opacity: 1; }
                             100% { transform: translateY(3px); opacity: 0; } }
        @keyframes spcSnow { 0% { transform: translateY(-2px); opacity: 0; }
                             50% { opacity: 1; }
                             100% { transform: translateY(4px); opacity: 0; } }
        @keyframes spcBolt { 0%,88%,100% { opacity: .4; } 90% { opacity: 1; } }
        @keyframes spcFog  { 0%,100% { transform: translateX(-1px); opacity: .7; }
                             50% { transform: translateX(1px); opacity: 1; } }
        @keyframes spcWind { 0%,100% { transform: translateX(-0.5px); }
                             50% { transform: translateX(1px); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
}
